import { useState, useEffect, useRef } from "react";
import { Button } from "../ui/Button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { SimpleCalendar } from "../ui/SimpleCalendar";
import { CalendarIcon, PlusIcon, UserIcon, Check, ChevronsUpDown, Search, ChevronLeft } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { format } from "date-fns";
import { el } from "date-fns/locale";
import { cn, customerMatchesSearch } from "../../lib/utils";
import {
  getReservationNotesForDisplay,
  isPlaceholderWalkInReservationNote,
} from "../../lib/customerClassification";
import { translations } from "../../lib/i18n";
import type { CreateReservationData, Customer, Table, Shift, Reservation } from "../../types";
import { useIsMobile } from "../../hooks/useMediaQuery";

// Validation schema — shift_id is auto-set from time, never shown to user
const reservationFormSchema = z.object({
  customer_id: z.string().min(1, "Ο πελάτης είναι υποχρεωτικός"),
  shift_id: z.string().min(1, "Η βάρδια είναι υποχρεωτική"),
  date: z.date({
    required_error: "Η ημερομηνία είναι υποχρεωτική",
  }),
  time: z.string().min(1, "Η ώρα είναι υποχρεωτική"),
  party_size: z.number().min(1, "Το μέγεθος ομάδας πρέπει να είναι τουλάχιστον 1").max(20, "Μέγιστο μέγεθος ομάδας: 20"),
  table_id: z.string().optional(),
  notes: z.string().optional(),
});

type ReservationFormData = z.infer<typeof reservationFormSchema>;

interface ReservationFormProps {
  customers: Customer[];
  tables: Table[];
  shifts: Shift[];
  reservations: Reservation[];
  onSubmit: (data: CreateReservationData) => Promise<void>;
  onClose?: () => void;
  isLoading?: boolean;
  triggerButton?: React.ReactNode;
  editingReservation?: Reservation | null;
  onCancel?: () => void;
  initialOpen?: boolean;
}

// Merge time slots from ALL active shifts, sorted chronologically
const generateAllTimeSlots = (shifts: Shift[]): string[] => {
  const slotsSet = new Set<string>();
  const activeShifts = shifts.filter(s => s.is_active !== false);

  for (const shift of activeShifts) {
    const [startH, startM] = shift.start_time.split(':').map(Number);
    const [endH, endM] = shift.end_time.split(':').map(Number);
    let current = startH * 60 + (startM || 0);
    const end = endH * 60 + (endM || 0);
    while (current < end) {
      const h = Math.floor(current / 60);
      const m = current % 60;
      slotsSet.add(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
      current += 30;
    }
  }

  return Array.from(slotsSet).sort();
};

// Find which shift covers a given time (used to silently set shift_id)
const detectShiftFromTime = (time: string, shifts: Shift[]): Shift | undefined => {
  if (!time) return undefined;
  const [h, m] = time.split(':').map(Number);
  const minutes = h * 60 + (m || 0);
  return shifts.find(shift => {
    const [sH, sM] = shift.start_time.split(':').map(Number);
    const [eH, eM] = shift.end_time.split(':').map(Number);
    return minutes >= sH * 60 + (sM || 0) && minutes < eH * 60 + (eM || 0);
  });
};

// Step indicator — pill for current, dot for others
const StepIndicator = ({ current }: { current: 1 | 2 | 3 }) => (
  <div className="flex items-center justify-center gap-1.5 mb-0.5">
    {([1, 2, 3] as const).map((s) => (
      <div
        key={s}
        className={cn(
          "h-1.5 rounded-full transition-[width,background-color] duration-200",
          s === current ? "w-6 bg-primary" : s < current ? "w-1.5 bg-primary/40" : "w-1.5 bg-muted-foreground/25"
        )}
      />
    ))}
  </div>
);

const STEP_TITLES: Record<1 | 2 | 3, string> = {
  1: "Πότε;",
  2: "Ποιος;",
  3: "Λεπτομέρειες",
};

// Inline client combobox: when open, dropdown replaces the trigger button in the same slot
interface ClientComboboxInlineProps {
  field: { value: string; onChange: (v: string) => void };
  customers: Customer[];
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  openCombobox: boolean;
  setOpenCombobox: (v: boolean) => void;
  setShowNewCustomer: (v: boolean) => void;
  setNewCustomerData: React.Dispatch<React.SetStateAction<{ name: string; phone: string; notes: string }>>;
  deriveCustomerInputFromSearch: (term: string) => { name: string; phone: string };
}

function ClientComboboxInline({
  field,
  customers,
  searchTerm,
  setSearchTerm,
  openCombobox,
  setOpenCombobox,
  setShowNewCustomer,
  setNewCustomerData,
  deriveCustomerInputFromSearch,
}: ClientComboboxInlineProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!openCombobox) return;
    const handlePointerDown = (e: PointerEvent) => {
      const el = wrapperRef.current;
      if (el && !el.contains(e.target as Node)) setOpenCombobox(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [openCombobox, setOpenCombobox]);

  if (!openCombobox) {
    return (
      <div ref={wrapperRef}>
        <FormControl>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={false}
            className={cn(
              "w-full h-12 justify-between pl-4 text-left font-normal text-base",
              !field.value && "text-muted-foreground"
            )}
            onClick={() => setOpenCombobox(true)}
          >
            {field.value
              ? customers.find((c) => c.id === field.value)?.name || "Επιλέξτε πελάτη"
              : "Επιλέξτε πελάτη"}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </FormControl>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="w-full rounded-md border bg-popover text-popover-foreground shadow-md overflow-hidden flex flex-col min-h-0 max-h-[320px]">
      <div className="flex flex-col gap-2 border-b flex-shrink-0 px-3 pb-2 pt-2 bg-popover">
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 shrink-0 opacity-50" />
          <input
            type="text"
            className="flex h-9 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            placeholder="Αναζήτηση με όνομα ή τηλέφωνο..."
            value={searchTerm}
            onChange={(e) => { e.stopPropagation(); setSearchTerm(e.target.value); }}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            autoFocus
          />
        </div>
        <button
          type="button"
          className="flex w-full cursor-pointer select-none items-center justify-center rounded-md border border-border bg-accent/90 py-2 text-sm font-semibold text-accent-foreground transition hover:bg-accent"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowNewCustomer(true);
            field.onChange("new_customer");
            setOpenCombobox(false);
            const { name, phone } = deriveCustomerInputFromSearch(searchTerm);
            setNewCustomerData(prev => ({ ...prev, name: name || prev.name, phone: phone || prev.phone }));
            setSearchTerm("");
          }}
        >
          <PlusIcon className="mr-2 h-4 w-4" />
          Δημιουργία νέου πελάτη
        </button>
      </div>
      <div
        className="min-h-0 max-h-[240px] overflow-y-auto overscroll-y-contain px-1 pt-1 pb-3 touch-pan-y"
        onWheel={(e) => e.stopPropagation()}
      >
        {(() => {
          const seenPhones = new Set<string>();
          const uniqueCustomers = customers.filter(c => {
            if (!c.phone) return true;
            const n = c.phone.trim();
            if (seenPhones.has(n)) return false;
            seenPhones.add(n);
            return true;
          });
          const filtered = uniqueCustomers.filter((c) => customerMatchesSearch(c, searchTerm));
          if (filtered.length === 0)
            return <div className="py-6 text-center text-sm text-muted-foreground">Δεν βρέθηκαν πελάτες.</div>;
          return filtered.map(c => (
            <button
              key={c.id}
              type="button"
              className={cn(
                "relative flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
                field.value === c.id && "bg-accent text-accent-foreground"
              )}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); field.onChange(c.id); setOpenCombobox(false); setSearchTerm(""); }}
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
            >
              <Check className={cn("mr-2 h-4 w-4", field.value === c.id ? "opacity-100" : "opacity-0")} />
              <div className="flex flex-col text-left">
                <span className="font-medium">{c.name}</span>
                <span className="text-xs text-muted-foreground">{c.phone}</span>
              </div>
            </button>
          ));
        })()}
      </div>
    </div>
  );
}

export function ReservationForm({
  customers,
  tables,
  shifts,
  reservations,
  onSubmit,
  onClose,
  isLoading = false,
  triggerButton,
  editingReservation,
  onCancel,
  initialOpen = false
}: ReservationFormProps) {
  const [open, setOpen] = useState(initialOpen);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  /** Inline calendar inside the dialog (same UX as PWA / mobile — no floating popover). */
  const [showInlineDateCalendar, setShowInlineDateCalendar] = useState(false);
  const [showMobileTimePicker, setShowMobileTimePicker] = useState(false);
  const [showMobileCustomerPicker, setShowMobileCustomerPicker] = useState(false);
  const [showMobilePartySizePicker, setShowMobilePartySizePicker] = useState(false);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [availableTables, setAvailableTables] = useState<Table[]>([]);
  const [hasAnyUnoccupiedTableAtSlot, setHasAnyUnoccupiedTableAtSlot] = useState(false);
  const dialogContentRef = useRef<HTMLDivElement | null>(null);
  const [openCombobox, setOpenCombobox] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    phone: '',
    notes: ''
  });

  const isMobile = useIsMobile();

  const form = useForm<ReservationFormData>({
    resolver: zodResolver(reservationFormSchema),
    defaultValues: {
      customer_id: "",
      shift_id: "",
      time: "",
      party_size: 2,
      table_id: "",
      notes: "",
    },
  });

  // Pre-fill form when editing
  useEffect(() => {
    if (editingReservation) {
      setOpen(true);
      setStep(1);
      form.reset({
        customer_id: editingReservation.customer_id,
        shift_id: editingReservation.shift_id,
        date: new Date(editingReservation.date),
        time: editingReservation.time,
        party_size: editingReservation.party_size,
        table_id: editingReservation.table_id || "auto",
        // Hide system walk-in marker in the field so managers can add real notes in an empty box
        notes: getReservationNotesForDisplay(editingReservation.notes) ?? "",
      });
    }
  }, [editingReservation, form]);

  useEffect(() => {
    if (initialOpen) setOpen(true);
  }, [initialOpen]);

  const selectedDate = form.watch("date");
  const selectedTime = form.watch("time");
  const selectedShift = form.watch("shift_id");
  const partySize = form.watch("party_size");
  const customerId = form.watch("customer_id");

  // Step 1 navigation guard
  const canProceedStep1 = !!selectedDate && !!selectedTime;
  // Step 2 navigation guard
  const canProceedStep2 = !!customerId && !!partySize;

  const allTimeSlots = generateAllTimeSlots(shifts);

  // Compute available tables whenever key fields change
  useEffect(() => {
    if (selectedDate && selectedTime && selectedShift && partySize && tables.length > 0) {
      const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
      const isTableBlockedAtSelectedSlot = (tableId: string) => reservations.some(reservation => {
        const assignedTableIds = Array.isArray(reservation.table_ids) && reservation.table_ids.length > 0
          ? reservation.table_ids
          : reservation.table_id
            ? [reservation.table_id]
            : [];
        if (
          !assignedTableIds.includes(tableId) ||
          reservation.date !== selectedDateStr ||
          (reservation.status !== 'confirmed' && reservation.status !== 'in-progress') ||
          reservation.id === editingReservation?.id
        ) return false;
        const [resHour, resMin] = reservation.time.split(':').map(Number);
        const [selHour, selMin] = selectedTime.split(':').map(Number);
        const resStart = resHour * 60 + resMin;
        const selStart = selHour * 60 + selMin;
        return selStart >= resStart && selStart < resStart + 125;
      });

      const isTableBookable = (status: string) =>
        status === 'available' || status === 'active';

      const unoccupiedTables = tables.filter(table => {
        if (!isTableBookable(table.status)) return false;
        return !isTableBlockedAtSelectedSlot(table.id);
      });

      const suitableTables = unoccupiedTables.filter((table) => table.seats >= partySize);
      setHasAnyUnoccupiedTableAtSlot(unoccupiedTables.length > 0);
      setAvailableTables(suitableTables);
    } else {
      setAvailableTables(tables.length > 0 ? [] : tables);
      setHasAnyUnoccupiedTableAtSlot(false);
    }
  }, [selectedDate, selectedTime, selectedShift, partySize, tables, reservations, editingReservation]);

  const resetDialog = () => {
    form.reset();
    setStep(1);
    setShowNewCustomer(false);
    setNewCustomerData({ name: '', phone: '', notes: '' });
    setShowInlineDateCalendar(false);
    setShowMobileTimePicker(false);
    setShowMobileCustomerPicker(false);
    setShowMobilePartySizePicker(false);
    setSearchTerm("");
  };

  const handleClose = () => {
    resetDialog();
    setOpen(false);
    onClose?.();
    if (editingReservation && onCancel) onCancel();
  };

  const handleNext = async () => {
    if (step === 1) {
      const valid = await form.trigger(["date", "time", "shift_id"]);
      if (valid && canProceedStep1) setStep(2);
    } else if (step === 2) {
      const valid = await form.trigger(["customer_id", "party_size"]);
      if (valid && canProceedStep2) setStep(3);
    }
  };

  const handleSubmit = async (data: ReservationFormData) => {
    try {
      if (data.customer_id === "new_customer") {
        if (!newCustomerData.name || !newCustomerData.phone) return;
      }

      const trimmedNotes = (data.notes ?? "").trim();
      const notesForSave = (() => {
        if (trimmedNotes) return trimmedNotes;
        if (editingReservation) {
          const prev = editingReservation.notes ?? "";
          if (isPlaceholderWalkInReservationNote(prev)) {
            return prev.trim() || "Walk-in";
          }
        }
        return "";
      })();

      const reservationData: CreateReservationData = {
        ...data,
        customer_id: data.customer_id,
        date: format(data.date, 'yyyy-MM-dd'),
        table_id: data.table_id === 'auto' ? '' : data.table_id,
        newCustomer: data.customer_id === "new_customer" ? newCustomerData : undefined,
        notes: notesForSave,
      };
      await onSubmit(reservationData);
      resetDialog();
      setOpen(false);
      if (editingReservation && onCancel) onCancel();
    } catch (error) {
      console.error('Error saving reservation:', error);
    }
  };

  const deriveCustomerInputFromSearch = (term: string) => {
    const trimmed = term.trim();
    if (!trimmed) return { name: '', phone: '' };
    const digits = trimmed.replace(/\D/g, '');
    const hasLetters = /[A-Za-zΑ-Ωα-ω]/.test(trimmed);
    const looksLikePhone = !hasLetters && digits.length >= 3;
    return { name: looksLikePhone ? '' : trimmed, phone: looksLikePhone ? trimmed : '' };
  };

  const quickPickDate = (preset: "today" | "tomorrow" | "dayAfterTomorrow") => {
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    const picked = new Date(base);
    if (preset === "tomorrow") {
      picked.setDate(picked.getDate() + 1);
    } else if (preset === "dayAfterTomorrow") {
      picked.setDate(picked.getDate() + 2);
    }
    form.setValue("date", picked, { shouldValidate: true, shouldDirty: true });
    setShowInlineDateCalendar(false);
  };

  const handleTimeSelection = (
    time: string,
    onChange: (value: string) => void
  ) => {
    onChange(time);
    const detected = detectShiftFromTime(time, shifts);
    if (detected) {
      form.setValue("shift_id", detected.id, { shouldValidate: true });
    }
    setShowMobileTimePicker(false);
  };

  const defaultTrigger = (
    <Button className="gap-2 h-11 min-w-[173px] px-4 text-base font-medium">
      <PlusIcon className="h-4 w-4" />
      {translations.reservations.new}
    </Button>
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose();
        else setOpen(true);
      }}
    >
      <style>{`
        .dark .rdp-caption_label,
        .dark .rdp-nav_button,
        .dark .rdp-head_cell,
        .dark .rdp-caption { color: white !important; }
        .dark .rdp-nav_button svg { fill: white !important; color: white !important; }
        .dark .rdp-dropdown_root { color: white !important; border-color: rgba(255,255,255,0.2) !important; }
      `}</style>

      {!editingReservation && (
        <DialogTrigger asChild>
          {triggerButton || defaultTrigger}
        </DialogTrigger>
      )}

      <DialogContent
        ref={dialogContentRef}
        className={cn(
          "max-w-none w-[calc(100vw-1rem)] max-h-[92vh] overflow-y-auto rounded-t-2xl rounded-b-none left-1/2 right-auto top-auto bottom-0 -translate-x-1/2 translate-y-0 p-4 pt-3 pb-4 gap-0 flex flex-col",
          "sm:w-[calc(100vw-2rem)] sm:max-w-[432px] sm:max-h-[90vh] sm:rounded-b-lg sm:rounded-t-lg sm:top-[50%] sm:bottom-auto sm:translate-y-[-50%]",
          isMobile && "min-h-0 pb-14"
        )}
      >
        <DialogHeader className="pb-2">
          <StepIndicator current={step} />
          <DialogTitle className="flex items-center gap-2 text-lg">
            <UserIcon className="h-5 w-5 shrink-0" />
            <span>
              {editingReservation ? "Επεξεργασία Κράτησης" : translations.reservations.new}
              <span className="ml-2 text-sm font-normal text-muted-foreground">— {STEP_TITLES[step]}</span>
            </span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Συμπληρώστε τα στοιχεία για τη νέα κράτηση σε 3 βήματα.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">

            {/* ─── STEP 1: WHEN ─── */}
            {step === 1 && (
              <div className="flex flex-col gap-5">
                {/* Date */}
                <FormField
                  control={form.control}
                  name="date"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-1.5">
                      <FormLabel className="text-base font-semibold">Ημερομηνία</FormLabel>

                      <div className="flex flex-col gap-3">
                        {!showInlineDateCalendar ? (
                          <FormControl>
                            <Button
                              type="button"
                              variant="outline"
                              className={cn(
                                "w-full h-14 pl-4 text-left font-normal text-base border-2 justify-start",
                                !field.value && "text-muted-foreground"
                              )}
                              onClick={() => setShowInlineDateCalendar(true)}
                            >
                              {field.value
                                ? format(field.value, "EEEE, d MMMM yyyy", { locale: el })
                                : "Επιλέξτε ημερομηνία"}
                              <CalendarIcon className="ml-auto h-5 w-5 shrink-0 opacity-50" />
                            </Button>
                          </FormControl>
                        ) : (
                          <div className="rounded-xl bg-muted/20 p-2 transition-opacity animate-in fade-in zoom-in-95 duration-200 w-full">
                            <SimpleCalendar
                              selected={field.value}
                              onSelect={(d) => {
                                field.onChange(d);
                                if (d) setShowInlineDateCalendar(false);
                              }}
                              disabled={(date) => {
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                return date < today || date < new Date("1900-01-01");
                              }}
                              className="border-0 shadow-none bg-transparent"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              className="w-full mt-2 h-10 text-sm text-muted-foreground hover:bg-background/50"
                              onClick={() => setShowInlineDateCalendar(false)}
                            >
                              Κλείσιμο ημερολογίου
                            </Button>
                          </div>
                        )}

                        {!showInlineDateCalendar && (
                          <div className="flex flex-wrap gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant={field.value && format(field.value, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd') ? "default" : "outline"}
                              className="h-9 rounded-full px-4 text-xs font-medium transition-[background-color,border-color,transform] border-2"
                              onClick={() => quickPickDate("today")}
                            >
                              Σήμερα
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant={field.value && format(field.value, 'yyyy-MM-dd') === format(new Date(new Date().setDate(new Date().getDate() + 1)), 'yyyy-MM-dd') ? "default" : "outline"}
                              className="h-9 rounded-full px-4 text-xs font-medium transition-[background-color,border-color,transform] border-2"
                              onClick={() => quickPickDate("tomorrow")}
                            >
                              Αύριο
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant={field.value && format(field.value, 'yyyy-MM-dd') === format(new Date(new Date().setDate(new Date().getDate() + 2)), 'yyyy-MM-dd') ? "default" : "outline"}
                              className="h-9 rounded-full px-4 text-xs font-medium transition-[background-color,border-color,transform] border-2"
                              onClick={() => quickPickDate("dayAfterTomorrow")}
                            >
                              Μεθαύριο
                            </Button>
                          </div>
                        )}
                      </div>

                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Time — shows all shifts merged; auto-detects shift_id */}
                <FormField
                  control={form.control}
                  name="time"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-1.5">
                      <FormLabel className="text-base font-semibold">Ώρα</FormLabel>
                      <div className="flex flex-col gap-3">
                        {!showMobileTimePicker ? (
                          <FormControl>
                            <Button
                              type="button"
                              variant="outline"
                              className={cn(
                                "w-full h-14 pl-4 text-left font-normal text-base border-2 justify-start",
                                !field.value && "text-muted-foreground"
                              )}
                              onClick={() => setShowMobileTimePicker(true)}
                            >
                              {field.value || "Επιλέξτε ώρα"}
                              <ChevronsUpDown className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        ) : (
                          <div className="rounded-xl bg-muted/20 p-2 transition-opacity animate-in fade-in zoom-in-95 duration-200">
                            <div className="max-h-56 overflow-y-auto pr-1">
                              {allTimeSlots.length === 0 ? (
                                <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                                  Δεν υπάρχουν διαθέσιμες ώρες
                                </div>
                              ) : (
                                <div className="grid grid-cols-3 gap-2">
                                  {allTimeSlots.map((t) => (
                                    <Button
                                      key={t}
                                      type="button"
                                      size="sm"
                                      variant={field.value === t ? "default" : "outline"}
                                      className={cn(
                                        "h-10 rounded-md text-sm font-medium",
                                        field.value !== t && "border-2"
                                      )}
                                      onClick={() => handleTimeSelection(t, field.onChange)}
                                    >
                                      {t}
                                    </Button>
                                  ))}
                                </div>
                              )}
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              className="w-full mt-2 h-10 text-sm text-muted-foreground hover:bg-background/50"
                              onClick={() => setShowMobileTimePicker(false)}
                            >
                              Κλείσιμο ώρας
                            </Button>
                          </div>
                        )}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Hidden shift_id field — just for validation feedback */}
                <FormField
                  control={form.control}
                  name="shift_id"
                  render={() => (
                    <FormItem className="hidden">
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="pt-1">
                  <Button
                    type="button"
                    className="w-full h-12 text-base"
                    onClick={handleNext}
                    disabled={!canProceedStep1}
                  >
                    Επόμενο
                    <ChevronLeft className="ml-2 h-4 w-4 rotate-180" />
                  </Button>
                </div>
              </div>
            )}

            {/* ─── STEP 2: WHO ─── */}
            {step === 2 && (
              <div className="space-y-4">
                {/* Customer */}
                <FormField
                  control={form.control}
                  name="customer_id"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel className="text-base">Πελάτης</FormLabel>
                      {!showNewCustomer ? (
                        isMobile ? (
                          <div className="flex flex-col gap-3">
                            {!showMobileCustomerPicker ? (
                              <FormControl>
                                <Button
                                  type="button"
                                  variant="outline"
                                  role="combobox"
                                  aria-expanded={showMobileCustomerPicker}
                                  className={cn(
                                    "w-full h-12 justify-between pl-4 text-left font-normal text-base border-2",
                                    !field.value && "text-muted-foreground"
                                  )}
                                  onClick={() => setShowMobileCustomerPicker(true)}
                                >
                                  {field.value
                                    ? customers.find((c) => c.id === field.value)?.name || "Επιλέξτε πελάτη"
                                    : "Επιλέξτε πελάτη"}
                                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                </Button>
                              </FormControl>
                            ) : (
                              <div className="rounded-xl bg-muted/20 p-2 transition-opacity animate-in fade-in zoom-in-95 duration-200">
                                <div className="flex flex-col gap-2 border-b px-2 pb-2 pt-1 bg-background/40 rounded-md">
                                  <div className="flex items-center gap-2">
                                    <Search className="h-4 w-4 shrink-0 opacity-50" />
                                    <input
                                      type="text"
                                      className="flex h-9 w-full rounded-md bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground"
                                      placeholder="Αναζήτηση με όνομα ή τηλέφωνο..."
                                      value={searchTerm}
                                      onChange={(e) => setSearchTerm(e.target.value)}
                                      autoFocus
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    className="flex w-full cursor-pointer select-none items-center justify-center rounded-md border border-border bg-accent/90 py-2 text-sm font-semibold text-accent-foreground transition hover:bg-accent"
                                    onClick={() => {
                                      setShowNewCustomer(true);
                                      field.onChange("new_customer");
                                      setShowMobileCustomerPicker(false);
                                      const { name, phone } = deriveCustomerInputFromSearch(searchTerm);
                                      setNewCustomerData(prev => ({
                                        ...prev,
                                        name: name || prev.name,
                                        phone: phone || prev.phone
                                      }));
                                      setSearchTerm("");
                                    }}
                                  >
                                    <PlusIcon className="mr-2 h-4 w-4" />
                                    Δημιουργία νέου πελάτη
                                  </button>
                                </div>
                                <div className="max-h-[220px] overflow-y-auto p-1 mt-2">
                                  {(() => {
                                    const seenPhones = new Set<string>();
                                    const uniqueCustomers = customers.filter(c => {
                                      if (!c.phone) return true;
                                      const n = c.phone.trim();
                                      if (seenPhones.has(n)) return false;
                                      seenPhones.add(n);
                                      return true;
                                    });
                                    const filtered = uniqueCustomers.filter((c) =>
                                      customerMatchesSearch(c, searchTerm)
                                    );
                                    if (filtered.length === 0)
                                      return <div className="py-6 text-center text-sm text-muted-foreground">Δεν βρέθηκαν πελάτες.</div>;
                                    return filtered.map(c => (
                                      <button
                                        key={c.id}
                                        type="button"
                                        className={cn(
                                          "relative flex w-full cursor-pointer select-none items-center rounded-md px-2 py-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground",
                                          field.value === c.id && "bg-accent text-accent-foreground"
                                        )}
                                        onClick={() => {
                                          field.onChange(c.id);
                                          setShowMobileCustomerPicker(false);
                                          setSearchTerm("");
                                        }}
                                      >
                                        <Check className={cn("mr-2 h-4 w-4", field.value === c.id ? "opacity-100" : "opacity-0")} />
                                        <div className="flex flex-col text-left">
                                          <span className="font-medium">{c.name}</span>
                                          <span className="text-xs text-muted-foreground">{c.phone}</span>
                                        </div>
                                      </button>
                                    ));
                                  })()}
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  className="w-full mt-2 h-10 text-sm text-muted-foreground hover:bg-background/50"
                                  onClick={() => setShowMobileCustomerPicker(false)}
                                >
                                  Κλείσιμο πελατών
                                </Button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <ClientComboboxInline
                            field={field}
                            customers={customers}
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            openCombobox={openCombobox}
                            setOpenCombobox={setOpenCombobox}
                            setShowNewCustomer={setShowNewCustomer}
                            setNewCustomerData={setNewCustomerData}
                            deriveCustomerInputFromSearch={deriveCustomerInputFromSearch}
                          />
                        )
                      ) : (
                        <div className="space-y-3 p-4 border rounded-lg bg-muted/50">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">Νέος πελάτης</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => { setShowNewCustomer(false); field.onChange(""); setNewCustomerData({ name: '', phone: '', notes: '' }); }}
                            >
                              Ακύρωση
                            </Button>
                          </div>
                          <div className="space-y-2">
                            <input
                              type="text"
                              placeholder="Όνομα*"
                              value={newCustomerData.name}
                              onChange={(e) => setNewCustomerData(p => ({ ...p, name: e.target.value }))}
                              className="w-full px-3 py-2.5 border rounded-md text-sm bg-slate-50 text-foreground dark:bg-background"
                              required
                            />
                            <input
                              type="tel"
                              placeholder="Τηλέφωνο*"
                              value={newCustomerData.phone}
                              onChange={(e) => setNewCustomerData(p => ({ ...p, phone: e.target.value }))}
                              className="w-full px-3 py-2.5 border rounded-md text-sm bg-slate-50 text-foreground dark:bg-background"
                              required
                            />
                            <input
                              type="text"
                              placeholder="Σημειώσεις (προαιρετικό)"
                              value={newCustomerData.notes}
                              onChange={(e) => setNewCustomerData(p => ({ ...p, notes: e.target.value }))}
                              className="w-full px-3 py-2.5 border rounded-md text-sm bg-slate-50 text-foreground dark:bg-background"
                            />
                          </div>
                        </div>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Party size */}
                <FormField
                  control={form.control}
                  name="party_size"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base">Μέγεθος ομάδας</FormLabel>
                      {isMobile ? (
                        <div className="flex flex-col gap-3">
                          {!showMobilePartySizePicker ? (
                            <FormControl>
                              <Button
                                type="button"
                                variant="outline"
                                className={cn(
                                  "w-full h-12 pl-4 text-left font-normal text-base border-2 justify-start",
                                  !field.value && "text-muted-foreground"
                                )}
                                onClick={() => setShowMobilePartySizePicker(true)}
                              >
                                {field.value
                                  ? `${field.value} ${field.value === 1 ? "άτομο" : "άτομα"}`
                                  : "Επιλέξτε άτομα"}
                                <ChevronsUpDown className="ml-auto h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          ) : (
                            <div className="rounded-xl bg-muted/20 p-2 transition-opacity animate-in fade-in zoom-in-95 duration-200">
                              <div className="max-h-[156px] overflow-y-auto pr-1 space-y-3">
                                <div>
                                  <p className="mb-2 text-xs font-semibold text-muted-foreground">Συχνά (1-10)</p>
                                  <div className="grid grid-cols-5 gap-2">
                                    {Array.from({ length: 10 }, (_, i) => i + 1).map((size) => (
                                      <Button
                                        key={size}
                                        type="button"
                                        size="sm"
                                        variant={field.value === size ? "default" : "outline"}
                                        className={cn(
                                          "h-10 rounded-md text-sm font-medium",
                                          field.value !== size && "border-2"
                                        )}
                                        onClick={() => {
                                          field.onChange(size);
                                          setShowMobilePartySizePicker(false);
                                        }}
                                      >
                                        {size}
                                      </Button>
                                    ))}
                                  </div>
                                </div>

                                <div className="pt-1 border-t border-border/50">
                                  <p className="mb-2 text-xs font-semibold text-muted-foreground">Περισσότερα (11-20)</p>
                                  <div className="grid grid-cols-5 gap-2">
                                    {Array.from({ length: 10 }, (_, i) => i + 11).map((size) => (
                                      <Button
                                        key={size}
                                        type="button"
                                        size="sm"
                                        variant={field.value === size ? "default" : "outline"}
                                        className={cn(
                                          "h-10 rounded-md text-sm font-medium",
                                          field.value !== size && "border-2"
                                        )}
                                        onClick={() => {
                                          field.onChange(size);
                                          setShowMobilePartySizePicker(false);
                                        }}
                                      >
                                        {size}
                                      </Button>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                className="w-full mt-2 h-10 text-sm text-muted-foreground hover:bg-background/50"
                                onClick={() => setShowMobilePartySizePicker(false)}
                              >
                                Κλείσιμο ατόμων
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <Select onValueChange={(v) => field.onChange(Number(v))} value={field.value?.toString()}>
                          <FormControl>
                            <SelectTrigger className="h-12 text-base">
                              <SelectValue placeholder="Επιλέξτε άτομα" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="max-h-56 overflow-y-auto">
                            {Array.from({ length: 20 }, (_, i) => i + 1).map((size) => (
                              <SelectItem key={size} value={size.toString()} className="text-base py-2.5">
                                {size} {size === 1 ? 'άτομο' : 'άτομα'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 h-12 text-base"
                    onClick={() => setStep(1)}
                  >
                    <ChevronLeft className="mr-2 h-4 w-4" />
                    Πίσω
                  </Button>
                  <Button
                    type="button"
                    className="flex-1 h-12 text-base"
                    onClick={handleNext}
                    disabled={!canProceedStep2}
                  >
                    Επόμενο
                    <ChevronLeft className="ml-2 h-4 w-4 rotate-180" />
                  </Button>
                </div>
              </div>
            )}

            {/* ─── STEP 3: DETAILS ─── */}
            {step === 3 && (
              <div className="space-y-4">
                {/* Summary chip */}
                <div className="rounded-lg border bg-muted/40 px-4 py-3 text-sm text-muted-foreground space-y-0.5">
                  {selectedDate && (
                    <div><span className="font-medium text-foreground">{format(selectedDate, "EEEE d MMMM", { locale: el })}</span> στις <span className="font-medium text-foreground">{selectedTime}</span></div>
                  )}
                  {customerId && (
                    <div>
                      {customerId === "new_customer"
                        ? <span className="font-medium text-foreground">{newCustomerData.name || "Νέος πελάτης"}</span>
                        : <span className="font-medium text-foreground">{customers.find(c => c.id === customerId)?.name}</span>
                      }
                      {" · "}{partySize} {partySize === 1 ? 'άτομο' : 'άτομα'}
                    </div>
                  )}
                </div>

                {/* Table */}
                <FormField
                  control={form.control}
                  name="table_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base">Τραπέζι <span className="text-muted-foreground font-normal">(προαιρετικό)</span></FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="h-12 text-base">
                            <SelectValue placeholder="Αυτόματη επιλογή" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="max-h-56 overflow-y-auto">
                          <SelectItem value="auto" className="text-base py-2.5">Αυτόματη επιλογή</SelectItem>
                          {availableTables.map((table) => (
                            <SelectItem key={table.id} value={table.id} className="text-base py-2.5">
                              Τραπέζι {table.number} ({table.seats} άτομα)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedDate && selectedTime && availableTables.length === 0 && (
                        <p className="text-sm text-muted-foreground mt-1.5 leading-snug break-words [overflow-wrap:anywhere]">
                          {hasAnyUnoccupiedTableAtSlot
                            ? 'Δεν υπάρχει μεμονωμένο τραπέζι που να χωράει την παρέα. Μπορείτε να συνεχίσετε χωρίς ανάθεση.'
                            : 'Δεν υπάρχουν διαθέσιμα τραπέζια για αυτή την ώρα'}
                        </p>
                      )}
                      {(!selectedDate || !selectedTime) && tables.length > 0 && (
                        <p className="text-sm text-muted-foreground mt-1.5 leading-snug break-words [overflow-wrap:anywhere]">
                          Επιλέξτε ημερομηνία και ώρα
                        </p>
                      )}
                      <FormMessage />
                      {selectedDate && selectedTime && tables.length > 0 && availableTables.length === 0 && !hasAnyUnoccupiedTableAtSlot && (
                        <p className="text-sm text-amber-600 bg-amber-50 dark:bg-amber-950/30 p-2 rounded-md border border-amber-200 dark:border-amber-800">
                          Όλα τα τραπέζια είναι κρατημένα για αυτή την ώρα (2ωρη διάρκεια + 5λ καθαρισμός).
                        </p>
                      )}
                    </FormItem>
                  )}
                />

                {/* Notes */}
                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base">Σημειώσεις <span className="text-muted-foreground font-normal">(προαιρετικό)</span></FormLabel>
                      <FormControl>
                        <textarea
                          {...field}
                          className="min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2.5 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
                          placeholder="Ειδικές παρατηρήσεις, αλλεργίες, κ.λπ."
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 h-12 text-base"
                    onClick={() => setStep(2)}
                    disabled={isLoading}
                  >
                    <ChevronLeft className="mr-2 h-4 w-4" />
                    Πίσω
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 h-12 text-base"
                    disabled={isLoading}
                  >
                    {isLoading
                      ? "Αποθήκευση..."
                      : editingReservation
                        ? "Ενημέρωση"
                        : "Δημιουργία κράτησης"}
                  </Button>
                </div>
              </div>
            )}

          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
